import React, { useCallback, useEffect, useState } from "react";
import { Bell, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const kindTone = (k) => ({
    success: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    warning: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    info: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    passport: "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30",
    error: "bg-destructive/10 text-destructive border-destructive/30",
}[k] || "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30");

export default function NotificationsCenter() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [kind, setKind] = useState("any");
    const [limit, setLimit] = useState("100");

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = { limit };
            if (kind !== "any") params.kind = kind;
            const { data } = await api.get("/admin/notifications-center", { params });
            setRows(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [kind, limit]);

    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="admin-notifications-center">
            <div className="mb-6">
                <p className="overline">Command Center</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Notifications Center
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Every notification sent across the platform, newest first.</p>
            </div>

            <div className="card-soft p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select value={kind} onValueChange={setKind}>
                    <SelectTrigger className="h-9" data-testid="notif-kind"><SelectValue placeholder="All kinds" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">All kinds</SelectItem>
                        <SelectItem value="success">Success</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="info">Info</SelectItem>
                        <SelectItem value="passport">Passport</SelectItem>
                        <SelectItem value="error">Error</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={limit} onValueChange={setLimit}>
                    <SelectTrigger className="h-9" data-testid="notif-limit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="50">Latest 50</SelectItem>
                        <SelectItem value="100">Latest 100</SelectItem>
                        <SelectItem value="250">Latest 250</SelectItem>
                        <SelectItem value="500">Latest 500</SelectItem>
                    </SelectContent>
                </Select>
                <button onClick={load} className="btn-outline text-sm" data-testid="notif-refresh">Refresh</button>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : rows.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <Bell size={22} className="mx-auto text-muted-foreground" />
                    <div className="mt-2 font-heading font-semibold">Nothing yet.</div>
                </div>
            ) : (
                <div className="card-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                                <th className="p-3 font-semibold">When</th>
                                <th className="p-3 font-semibold">Kind</th>
                                <th className="p-3 font-semibold">Recipient</th>
                                <th className="p-3 font-semibold">Title / Body</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((n) => (
                                <tr key={n.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30" data-testid={`notif-row-${n.id}`}>
                                    <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                                        {n.created_at ? new Date(n.created_at).toLocaleString() : "—"}
                                    </td>
                                    <td className="p-3">
                                        <Badge className={`text-[10px] border ${kindTone(n.kind)}`}>{n.kind || "info"}</Badge>
                                    </td>
                                    <td className="p-3 text-xs">
                                        <div className="text-foreground truncate max-w-[220px]">{n.recipient_email || "—"}</div>
                                        {n.recipient_role && <div className="text-[10px] text-muted-foreground">{n.recipient_role}</div>}
                                    </td>
                                    <td className="p-3 text-xs">
                                        <div className="font-semibold text-foreground">{n.title}</div>
                                        <div className="text-muted-foreground line-clamp-2">{n.body}</div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
